import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Input,
  Space,
  Select,
  Upload,
  Divider,
  Flex,
} from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";

const HotelDetailsEditor = ({
  slide,
  updateSlide,
  removeSlide,
  triggerSave,
}) => {
  const [hotelGroups, setHotelGroups] = useState(
    slide.hotelGroups || [{ groupName: "Hotel Option 01", hotels: [] }]
  );
  const [background, setBackground] = useState(slide.bg || "");
  const [title, setTitle] = useState(slide.title || "HOTEL DETAILS");
  const [subtitle, setSubtitle] = useState(slide.subtitle || "");

  useEffect(() => {
    setHotelGroups(
      slide.hotelGroups || [{ groupName: "Hotel Option 01", hotels: [] }]
    );
    setBackground(slide.bg || "");
    setTitle(slide.title || "HOTEL DETAILS");
    setSubtitle(slide.subtitle || "");
  }, [slide]);

  useEffect(() => {
    updateSlide(slide.id, {
      ...slide,
      hotelGroups,
      title,
      subtitle,
      bg: background,
    });
  }, [triggerSave]);

  const handleBackgroundChange = (info) => {
    const file = info.file.originFileObj || info.file;
    if (file && file instanceof Blob) {
      const reader = new FileReader();
      reader.onload = () => setBackground(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const addHotel = (groupIndex) => {
    const newHotel = {
      date: "",
      location: "",
      hotelName: "",
      starCategory: "4 Star",
      roomType: "Double",
      numRooms: 1,
      mealPlan: "HB",
      roomCategory: "Standard",
    };
    const updatedGroups = [...hotelGroups];
    updatedGroups[groupIndex].hotels.push(newHotel);
    setHotelGroups(updatedGroups);
  };

  const updateHotel = (groupIndex, hotelIndex, field, value) => {
    const updatedGroups = [...hotelGroups];
    updatedGroups[groupIndex].hotels[hotelIndex][field] = value;
    setHotelGroups(updatedGroups);
  };

  const removeHotel = (groupIndex, hotelIndex) => {
    const updatedGroups = [...hotelGroups];
    updatedGroups[groupIndex].hotels.splice(hotelIndex, 1);
    setHotelGroups(updatedGroups);
  };

  const addHotelGroup = () => {
    if (hotelGroups.length >= 2) return;
    const newGroup = {
      groupName: `Hotel Option ${hotelGroups.length + 1}`,
      hotels: [],
    };
    setHotelGroups([...hotelGroups, newGroup]);
  };

  const removeHotelGroup = (groupIndex) => {
    const updatedGroups = [...hotelGroups];
    updatedGroups.splice(groupIndex, 1);
    const renumbered = updatedGroups.map((g, i) => ({
      ...g,
      groupName: `Hotel Option ${String(i + 1).padStart(2, "0")}`,
    }));
    setHotelGroups(renumbered);
  };

  return (
    <div className="border rounded-lg p-4 bg-white shadow-sm">
      <Divider />
      <h1 className="font-semibold mb-4 text-lg">Edit Hotel Slide</h1>

      <Form layout="vertical">
        {/* 🔹 Upload Background */}
        <Form.Item label="Background Image" style={{ marginBottom: 12 }}>
          <Upload
            accept="image/*"
            beforeUpload={() => false}
            showUploadList={false}
            onChange={handleBackgroundChange}
          >
            <Button icon={<UploadOutlined />} type="primary">
              Upload Background
            </Button>
          </Upload>
          {background && (
            <img
              src={background}
              alt="Background Preview"
              className="block mt-2 w-full h-32 max-w-xs object-cover rounded-lg border"
            />
          )}
        </Form.Item>

        {/* 🔹 Slide Heading */}
        <Form.Item label="Slide Heading" style={{ marginBottom: 12 }}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Form.Item>

        {/* 🔹 Slide Tagline */}
        <Form.Item label="Slide Tagline" style={{ marginBottom: 12 }}>
          <Input
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
          />
        </Form.Item>

        {/* 🔹 Hotel Groups */}
        {hotelGroups.map((group, gIdx) => (
          <div key={gIdx} className="mb-6 border p-3 rounded bg-gray-50">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold">{group.groupName}</h3>
              {hotelGroups.length > 1 && (
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeHotelGroup(gIdx)}
                >
                  Remove Option
                </Button>
              )}
            </div>

            {group.hotels.map((hotel, hIdx) => (
              <div key={hIdx} className="mb-4 p-3 rounded bg-white border">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Input
                    placeholder="Date"
                    value={hotel.date}
                    onChange={(e) =>
                      updateHotel(gIdx, hIdx, "date", e.target.value)
                    }
                  />
                  <Input
                    placeholder="Location"
                    value={hotel.location}
                    onChange={(e) =>
                      updateHotel(gIdx, hIdx, "location", e.target.value)
                    }
                  />
                  <Input
                    placeholder="Hotel Name"
                    value={hotel.hotelName}
                    onChange={(e) =>
                      updateHotel(gIdx, hIdx, "hotelName", e.target.value)
                    }
                  />
                  <Select
                    value={hotel.starCategory}
                    options={[
                      { label: "1 Star", value: "1 Star" },
                      { label: "2 Star", value: "2 Star" },
                      { label: "3 Star", value: "3 Star" },
                      { label: "4 Star", value: "4 Star" },
                      { label: "5 Star", value: "5 Star" },
                    ]}
                    onChange={(v) => updateHotel(gIdx, hIdx, "starCategory", v)}
                  />
                  <Input
                    placeholder="Room Type"
                    value={hotel.roomType}
                    onChange={(e) =>
                      updateHotel(gIdx, hIdx, "roomType", e.target.value)
                    }
                  />
                  <Input
                    placeholder="Number of Rooms"
                    type="number"
                    value={hotel.numRooms}
                    onChange={(e) =>
                      updateHotel(gIdx, hIdx, "numRooms", e.target.value)
                    }
                  />
                  <Select
                    value={hotel.mealPlan}
                    options={[
                      { label: "HB", value: "HB" },
                      { label: "BB", value: "BB" },
                      { label: "CP", value: "CP" },
                      { label: "MAP", value: "MAP" },
                    ]}
                    onChange={(v) => updateHotel(gIdx, hIdx, "mealPlan", v)}
                  />
                  <Input
                    placeholder="Room Category"
                    value={hotel.roomCategory}
                    onChange={(e) =>
                      updateHotel(gIdx, hIdx, "roomCategory", e.target.value)
                    }
                  />
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeHotel(gIdx, hIdx)}
                  >
                    Remove Hotel
                  </Button>
                </Space>
              </div>
            ))}

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => addHotel(gIdx)}
            >
              Add Hotel to {group.groupName}
            </Button>
          </div>
        ))}

        {hotelGroups.length < 2 && (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addHotelGroup}
            className="mt-3"
          >
            Add Another Hotel Option
          </Button>
        )}

        <Flex justify="flex-end" className="mt-6">
          <Button type="primary" danger onClick={() => removeSlide(slide.id)}>
            <DeleteOutlined /> Remove Slide
          </Button>
        </Flex>
      </Form>
    </div>
  );
};

export default HotelDetailsEditor;
